/** Three-column layout component using CSS Flexbox with content projection. @component */

import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, input } from '@angular/core';
import { HxDivider } from '../divider/divider';
import type { CssClassInput, ThreeColumnLayoutGap } from './types';

/**
 * Responsive Flexbox layout with configurable gap. Desktop: 33.33/33.33/33.33 columns; Mobile: single column stack.
 * OnPush change detection. ViewEncapsulation disabled for global style access.
 *
 * @example
 *   ```html
 *   <hx-three-column-layout [gap]="'medium'">
 *     <div hxThreeColumnLeft>Left content</div>
 *     <div hxThreeColumnCenter>Center content</div>
 *     <div hxThreeColumnRight>Right content</div>
 *   </hx-three-column-layout>
 *   ```;
 *
 * @see {@link ThreeColumnLayoutGap} for gap size options
 * @see {@link CssClassInput} for custom class formats
 */
@Component({
  selector: 'hx-three-column-layout',
  templateUrl: 'three-column-layout.html',
  styleUrls: ['three-column-layout.css'],
  imports: [HxDivider],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class]': 'hostClasses()',
    '[style.--custom-gap-value]': 'customGapValue()',
  },
})
export class HxThreeColumnLayout {
  /**
   * Gap size between columns: small (16px), medium (24px), large (32px), or custom.
   *
   * @example
   *   ```html
   *   <hx-three-column-layout gap="medium">...</hx-three-column-layout>
   *   ```;
   *
   * @default 'small'
   */
  gap = input<ThreeColumnLayoutGap>('small');

  /**
   * Custom gap value (any CSS length unit) used when gap='custom'.
   *
   * @remarks
   *   Accepts any valid CSS length value (px, rem, em, %, vw, etc.). Falls back to medium gap (24px) if value is invalid
   *   or undefined.
   * @example
   *   ```html
   *   <hx-three-column-layout gap="custom" customGap="3rem">...</hx-three-column-layout>
   *   ```;
   */
  customGap = input<string | undefined>();

  /**
   * Additional CSS classes: string, array, or undefined. Appended to base component classes.
   *
   * @example
   *   ```html
   *   <hx-three-column-layout [cssClass]="'my-layout'">...</hx-three-column-layout>
   *   <hx-three-column-layout [cssClass]="['class-a', 'class-b']">...</hx-three-column-layout>
   *   ```;
   */
  cssClass = input<CssClassInput>();

  /**
   * Whether to render vertical separators between columns (left-center and center-right).
   *
   * @default false
   */
  showSeparator = input<boolean>(false);

  /**
   * Computed CSS class list: base class + gap variant + custom classes.
   *
   * @returns Space-separated string of CSS classes
   * @internal
   */
  protected hostClasses = computed(() => {
    const classes: string[] = ['hx-three-column-layout', `hx-three-column-layout--gap-${this.gap()}`];

    const custom = this.cssClass();
    if (custom) {
      classes.push(...(Array.isArray(custom) ? custom : [custom]));
    }

    return classes.join(' ');
  });

  /**
   * Custom gap value for inline style binding.
   *
   * @remarks
   *   Returns customGap value only when gap='custom'. Used for CSS variable binding: `--custom-gap-value`
   * @returns Custom gap value or undefined
   * @internal
   */
  protected customGapValue = computed(() => (this.gap() === 'custom' ? this.customGap() : undefined));
}
